import { Link } from "react-router-dom";
import { ArrowUpRight, XCircle, CheckCircle2 } from "lucide-react";
import { Reveal, Kicker } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";

const LIMITATIONS = [
  {
    n: "01",
    title: "Administration volume",
    body: "Activated charcoal requires administration volumes that are large relative to patient size, which complicates delivery in clinical settings and limits practicality in small-breed patients.",
  },
  {
    n: "02",
    title: "Palatability & compliance",
    body: "Poor palatability leads to refusal and incomplete administration. Achieving the intended intake in a conscious, distressed patient is frequently difficult for clinical staff and owners alike.",
  },
  {
    n: "03",
    title: "Aspiration pneumonia",
    body: "Aspiration of charcoal is a recognized and potentially fatal complication in veterinary practice, with risk elevated following emesis or in sedated or obtunded patients whose airway protection is compromised.",
  },
  {
    n: "04",
    title: "Sorbitol & hypernatremia",
    body: "Sorbitol-containing charcoal formulations carry a recognized risk of hypernatremia, adding an electrolyte-management burden to an already acute presentation.",
  },
  {
    n: "05",
    title: "GI blockage & impaction",
    body: "The large volumes required can contribute to gastrointestinal impaction and obstruction, a serious and potentially fatal complication, particularly in small patients or with repeated administration.",
  },
];

const CHARCOAL_RISKS = [
  "Administration volumes large relative to patient size",
  "Poor palatability; incomplete administration is common",
  "Aspiration pneumonia, a potentially fatal complication, with risk elevated after emesis or sedation",
  "GI impaction and blockage risk with large or repeated volumes, potentially fatal",
  "Hypernatremia risk from sorbitol-containing formulations",
];

const ACTIVE_PROFILE = [
  "Low administration volume in palatable, dosage-form-flexible formats",
  "No bulk liquid suspension to aspirate; the profile does not carry charcoal's aspiration pneumonia risk",
  "Non-systemic mineral that passes through the GI tract without the impaction risk of bulk charcoal",
  "Sorbitol-free by design; no associated hypernatremia risk",
  "Binding onset within seconds at canine gastric pH",
];

export default function Opportunity() {
  return (
    <div data-testid="opportunity-page">
      <section className="hero-grid pt-32 lg:pt-40 pb-16 lg:pb-20 border-b border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal y={16}>
            <Kicker index="§1">The Opportunity</Kicker>
            <h1 className="mt-6 font-headline font-semibold tracking-tight text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.06] max-w-4xl">
              A common emergency with recognized management constraints.
            </h1>
            <p className="mt-7 text-base sm:text-lg text-smoke leading-relaxed max-w-3xl">
              Chocolate ingestion is among the most common causes of canine poison-control calls
              worldwide, with reported incidence spiking around holidays. Current management
              relies on induced emesis followed by administration of activated charcoal.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-24" data-testid="opportunity-limitations">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Kicker>Activated charcoal · Recognized limitations</Kicker>
            <h2 className="mt-5 font-headline font-semibold tracking-tight text-ink text-2xl sm:text-3xl lg:text-4xl max-w-3xl leading-tight">
              Five constraints documented in veterinary practice.
            </h2>
            <p className="mt-5 text-sm text-faint max-w-2xl">
              Presented in neutral clinical terms, as reported in veterinary practice.
            </p>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line">
            {LIMITATIONS.map((l, i) => (
              <Reveal key={l.n} delay={i * 0.08} className="bg-white p-8 lg:p-10 hover:bg-pinelight/30 transition-colors duration-300">
                <p className="font-mono text-xs text-pine">{l.n}</p>
                <h3 className="mt-4 font-headline font-semibold text-xl text-ink tracking-tight">{l.title}</h3>
                <p className="mt-3 text-sm text-smoke leading-relaxed">{l.body}</p>
              </Reveal>
            ))}
            <Reveal delay={0.4} className="bg-pinelight/40 p-8 lg:p-10 flex items-center">
              <p className="text-sm text-smoke leading-relaxed">
                These constraints are documented for activated charcoal in veterinary practice.
                A targeted adsorbent engineered for this single toxicological target is designed
                to avoid them.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-line py-20 lg:py-24" data-testid="opportunity-comparison">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Kicker>Side by side</Kicker>
            <h2 className="mt-5 font-headline font-semibold tracking-tight text-ink text-2xl sm:text-3xl lg:text-4xl max-w-3xl leading-tight">
              A different risk profile by design.
            </h2>
          </Reveal>
          <div className="mt-12 grid lg:grid-cols-2 gap-px bg-line border border-line">
            <Reveal className="bg-paper p-8 lg:p-12">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-faint">Activated charcoal</p>
              <ul className="mt-7 space-y-5">
                {CHARCOAL_RISKS.map((r) => (
                  <li key={r} className="flex gap-3.5 text-sm text-smoke leading-relaxed">
                    <XCircle size={17} className="text-faint shrink-0 mt-0.5" strokeWidth={1.75} />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.1} className="bg-obsidian text-white p-8 lg:p-12">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-pinelight/80">The Chocolate Detox active</p>
              <ul className="mt-7 space-y-5">
                {ACTIVE_PROFILE.map((r) => (
                  <li key={r} className="flex gap-3.5 text-sm text-white/85 leading-relaxed">
                    <CheckCircle2 size={17} className="text-pinelight shrink-0 mt-0.5" strokeWidth={1.75} />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal delay={0.12}>
            <div className="mt-10 border-l-2 border-pine bg-pinelight/50 p-6 lg:p-8 max-w-4xl" data-testid="opportunity-validation-note">
              <p className="text-sm text-smoke leading-relaxed">
                <span className="text-ink font-medium">Independent validation.</span> Safety and
                efficacy have been demonstrated in gold-standard trials conducted by independent
                third-party contract research organizations (CROs) and university research programs.
                Trial summaries are available to qualified partners under NDA.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
          <Reveal>
            <h2 className="font-headline font-semibold tracking-tight text-ink text-2xl sm:text-3xl max-w-xl leading-tight">
              For use in products intended to support the veterinary management of methylxanthine ingestion.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/partnering"
              data-testid="opportunity-dossier-cta"
              className="inline-flex items-center gap-2 bg-pine hover:bg-pinehover text-white text-sm font-medium px-7 py-4 transition-colors duration-200 shrink-0"
            >
              Request the confidential dossier <ArrowUpRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
